import { captureException } from '@sentry/react'
import { z } from 'zod'
import axiosNode from '@/axios-instance-node'
import {
	CsvUpload,
	CsvUploadsResponse,
	FinalReviewListingDetails,
	FinalReviewListingsResponse,
	type FinalReviewRejectionReason,
	type FinalReviewStatus,
	LocationsResponse,
	LuxeFeatureFlagsResponse,
	type LuxeReviewerSection,
	MMDataOverview,
	MMParkingLotListingsResponse,
	MMSubmissionDetails,
	type MMSubmissionInventory,
	type MMSubmissionStatus,
	MMSubmissionsResponse,
	type PrecheckState,
	QueueListingDetails,
	QueueListingsResponse,
	QuoteGenerationListingsResponse,
	ReviewersResponse,
	SubmissionActivityEntry,
	type SubmissionSource,
	type SubmissionStatus,
	type SubmissionType,
	VisualVettingAccuracyResponse,
	VisualVettingListingDetails,
	VisualVettingListingsResponse,
	VisualVettingScoreResponse,
	VisualVettingVolumeResponse
} from '@/features/luxe-submissions/types'
import { encodeQueryParam, parseResponseData } from '@/helpers/utils'

export const ResolveAirbnbListingData = z.object({
	airbnbListingId: z.string(),
	listingName: z.string().nullable(),
	city: z.string().nullable(),
	bedrooms: z.number().nullable(),
	bathrooms: z.number().nullable(),
	beds: z.number().nullable(),
	personCapacity: z.number().nullable(),

	propertyType: z.string().nullable(),
	amenities: z.array(z.string()).nullable(),
	photos: z.array(z.string()),
	description: z.string().nullable(),
	hostName: z.string().nullable(),
	country: z.string().nullable(),
	hostIsSuperhost: z.boolean(),
	hostProfilePictureUrl: z.string().nullable(),
	lat: z.string().nullable(),
	lng: z.string().nullable(),
	roomType: z.string().nullable(),
	hostExternalId: z.string().nullable()
})
export type ResolveAirbnbListingData = z.infer<typeof ResolveAirbnbListingData>

export const httpGetListingData = async (listingUrl: string) => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/marketing-manager/listings/resolve',
		{ params: { url: listingUrl } }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: z.object({ data: ResolveAirbnbListingData }),
		onZodError: captureException
	})
}

export interface GetQueueListingsParams {
	page?: number
	perPage?: number
	sortBy?: string
	sortOrder?: 'asc' | 'desc'
	// Narrows the queue to the given statuses. Rejected rows are part of the
	// default population, so "show rejected only" is this filter rather than a
	// flag of its own. Takes precedence over the legacy `showRejected`.
	statuses?: SubmissionStatus[]
	city?: string
	// Singular and scalar, unlike the visual vetting queue's `submissionTypes`:
	// the pre-check endpoint filters on one type at a time.
	submissionType?: SubmissionType
	// Array-shaped to match the other queues' source filter, even though the UI
	// picks one at a time.
	sources?: SubmissionSource[]
	// Filters by Pre-check population; ignored unless luxe-vv2-ml-review or
	// luxe-vv2-vendor-precheck is on — the same condition under which the
	// `precheckState` field comes back on the rows.
	precheckState?: PrecheckState
}

export const httpGetQueueListings = async (
	params: GetQueueListingsParams = {}
) => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/precheck/submissions',
		{ params, paramsSerializer: { encode: encodeQueryParam } }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: QueueListingsResponse,
		onZodError: captureException
	})
}

export interface HttpGetQueueCountsOptions {
	params: {
		showRejected?: boolean
		// Keep these in step with the filtered list; pass the same values the
		// list is filtered by, or the tab count contradicts the rows below it.
		statuses?: SubmissionStatus[]
		precheckState?: PrecheckState
	}
}

export const httpGetQueueCounts = async (
	options: HttpGetQueueCountsOptions = { params: {} }
) => {
	const response = await axiosNode.get('v3/luminate/luxe/queue-counts', {
		params: options.params
	})

	return parseResponseData({
		responseData: response.data,
		responseShape: z.object({
			precheck: z.number(),
			visualVetting: z.number(),
			finalReview: z.number(),
			quoteGeneration: z.number()
		}),
		onZodError: captureException
	})
}

export interface GetLocationsParams {
	search?: string
	page?: number
}

export const httpGetLocations = async (params: GetLocationsParams = {}) => {
	const response = await axiosNode.get('v3/luminate/luxe/locations', {
		params
	})

	return parseResponseData({
		responseData: response.data,
		responseShape: LocationsResponse,
		onZodError: captureException
	})
}

export interface GetReviewersParams {
	section: LuxeReviewerSection
	search?: string
	page?: number
}

export const httpGetReviewers = async (params: GetReviewersParams) => {
	const response = await axiosNode.get('v3/luminate/luxe/reviewers', {
		params
	})

	return parseResponseData({
		responseData: response.data,
		responseShape: ReviewersResponse,
		onZodError: captureException
	})
}

export interface GetVisualVettingListingsParams {
	page?: number
	perPage?: number
	sortBy?: string
	sortOrder?: 'asc' | 'desc'
	locations?: string[]
	sources?: SubmissionSource[]
	submissionTypes?: SubmissionType[]
	statuses?: SubmissionStatus[]
	vetterIds?: string[]
	search?: string
	assignedToMe?: boolean
	// Gated by luxe-vv2-unassigned-filter: true returns only submissions nobody
	// has claimed or been assigned. Ignored server-side with the flag off.
	unassigned?: boolean
}

export const httpGetVisualVettingListings = async (
	params: GetVisualVettingListingsParams = {}
) => {
	const response = await axiosNode.get(
		'/v3/luminate/luxe/visual-vetting/submissions',
		{ params, paramsSerializer: { encode: encodeQueryParam } }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: VisualVettingListingsResponse,
		onZodError: captureException
	})
}

export interface GetQueueListingDetailsParams {
	listingId?: string
}
export const httpGetQueueListingDetails = async (
	params: GetQueueListingDetailsParams = {}
) => {
	const response = await axiosNode.get(
		`v3/luminate/luxe/precheck/submissions/${params.listingId}`,
		{ params }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: QueueListingDetails,
		onZodError: captureException
	})
}

export interface GetVisualVettingListingDetailsParams {
	listingId?: string
}
export const httpGetVisualVettingListingDetails = async (
	params: GetVisualVettingListingDetailsParams = {}
) => {
	const response = await axiosNode.get(
		`v3/luminate/luxe/visual-vetting/submissions/${params.listingId}`
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: VisualVettingListingDetails,
		onZodError: captureException
	})
}

export const httpClaimListing = (listingIds: string[]) => {
	return axiosNode.post('v3/luminate/luxe/visual-vetting/submissions/claim', {
		ids: listingIds
	})
}

export const httpAssignListing = (listingIds: string[], reviewerId: number) => {
	return axiosNode.post(
		'v3/luminate/luxe/visual-vetting/submissions/assign',
		{ ids: listingIds, reviewerId }
	)
}

export interface GetFinalReviewListingsParams {
	page?: number
	perPage?: number
	sortBy?: string
	sortOrder?: 'asc' | 'desc'
	locations?: string[]
	sources?: SubmissionSource[]
	submissionTypes?: SubmissionType[]
	statuses?: FinalReviewStatus[]
	assignedToMe?: boolean
	reviewerIds?: string[]
	vetterIds?: string[]
	search?: string
}

export const httpGetFinalReviewListings = async (
	params: GetFinalReviewListingsParams = {}
) => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/final-review/submissions',
		{ params, paramsSerializer: { encode: encodeQueryParam } }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: FinalReviewListingsResponse,
		onZodError: captureException
	})
}

export const httpClaimFinalReviewListing = (listingIds: string[]) => {
	return axiosNode.post('/v3/luminate/luxe/final-review/submissions/claim', {
		ids: listingIds
	})
}

export const httpAcceptQueueListing = (listingId: string) => {
	return axiosNode.post(
		`v3/luminate/luxe/precheck/submissions/${listingId}/approve`
	)
}

export const httpRejectQueueListing = (listingId: string) => {
	return axiosNode.post(
		`v3/luminate/luxe/precheck/submissions/${listingId}/reject`
	)
}

export const httpAssignFinalReviewListing = (
	listingIds: string[],
	reviewerId: string
) => {
	return axiosNode.post('/v3/luminate/luxe/final-review/submissions/assign', {
		ids: listingIds,
		reviewerId
	})
}

export const httpGetVisualVettingScore = async (listingId: string) => {
	const response = await axiosNode.get(
		`v3/luminate/luxe/visual-vetting/submissions/${listingId}/score`
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: VisualVettingScoreResponse,
		onZodError: captureException
	})
}

export interface SubmitVisualVettingListingOptions {
	urlParams: {
		listingId: string
	}
	payload: { isEscalated: boolean; note?: string }
}

export const httpSubmitVisualVettingListing = ({
	payload,
	urlParams
}: SubmitVisualVettingListingOptions): Promise<{ success: boolean }> => {
	return axiosNode.post(
		`v3/luminate/luxe/visual-vetting/submissions/${urlParams.listingId}/submit`,
		payload
	)
}

export interface SaveVisualVettingDraftOptions {
	urlParams: {
		listingId: string
	}
	payload: {
		answers: { questionId: string; answer: string | null }[]
	}
}

export const httpSaveVisualVettingDraft = ({
	payload,
	urlParams
}: SaveVisualVettingDraftOptions): Promise<{ success: boolean }> => {
	return axiosNode.put(
		`v3/luminate/luxe/visual-vetting/submissions/${urlParams.listingId}/answers`,
		payload
	)
}

export interface GetQuoteGenerationListingsParams {
	page?: number
	perPage?: number
	sortBy?: string
	sortOrder?: 'asc' | 'desc'
	locations?: string[]
	sources?: SubmissionSource[]
	submissionTypes?: SubmissionType[]
	finalReviewerIds?: string[]
	search?: string
}

export const httpGetQuoteGenerationListings = async (
	params: GetQuoteGenerationListingsParams = {}
) => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/quote-generation/submissions',
		{ params, paramsSerializer: { encode: encodeQueryParam } }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: QuoteGenerationListingsResponse,
		onZodError: captureException
	})
}

export const GenerateQuoteResponse = z.object({
	generated: z.array(
		z.object({
			submissionId: z.string(),
			quoteLink: z.string().nullable(),
			hostEmail: z.string().nullable()
		})
	),
	skipped: z.array(
		z.object({
			submissionId: z.string(),
			reason: z.string()
		})
	)
})

export type GenerateQuoteResponse = z.infer<typeof GenerateQuoteResponse>

export interface GenerateQuoteOptions {
	params: { submissionIds: string[]; blockEmail: boolean }
}

export const httpGenerateQuote = async ({ params }: GenerateQuoteOptions) => {
	const response = await axiosNode.post(
		'v3/luminate/luxe/quote-generation/submissions/generate',
		params
	)
	return parseResponseData({
		responseData: response.data,
		responseShape: GenerateQuoteResponse,
		onZodError: captureException
	})
}

export interface GetFinalReviewListingDetailsParams {
	listingId?: string
}

export const httpGetFinalReviewListingDetails = async (
	params: GetFinalReviewListingDetailsParams = {}
) => {
	const response = await axiosNode.get(
		`v3/luminate/luxe/final-review/submissions/${params.listingId}`
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: FinalReviewListingDetails,
		onZodError: captureException
	})
}

export interface RejectFinalReviewListingOptions {
	urlParams: {
		listingId: string
	}
	payload: {
		reasons: FinalReviewRejectionReason[]
		note?: string
	}
}
export const httpRejectFinalReviewListing = ({
	urlParams,
	payload
}: RejectFinalReviewListingOptions) => {
	return axiosNode.post(
		`v3/luminate/luxe/final-review/submissions/${urlParams.listingId}/reject`,
		payload
	)
}

export const httpApproveFinalReviewListing = (listingId: string) => {
	return axiosNode.post(
		`v3/luminate/luxe/final-review/submissions/${listingId}/approve`
	)
}

export interface GetMMSubmissionsParams {
	page?: number
	perPage?: number
	sortBy?: string
	sortOrder?: 'asc' | 'desc'
	location?: string
	listingInventory?: MMSubmissionInventory
	search?: string
	status?: string
}

export const httpGetMMSubmissions = async (
	params: GetMMSubmissionsParams = {}
) => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/marketing-manager/submissions',
		{ params, paramsSerializer: { encode: encodeQueryParam } }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: MMSubmissionsResponse,
		onZodError: captureException
	})
}

export interface GetMMSubmissionDetailsParams {
	listingId?: string
}

export const httpGetMMSubmissionDetails = async (
	params: GetMMSubmissionDetailsParams = {}
) => {
	const response = await axiosNode.get(
		`v3/luminate/luxe/marketing-manager/submissions/${params.listingId}`
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: z.object({ data: MMSubmissionDetails }),
		onZodError: captureException
	})
}

export interface GetMMParkingLotListingsParams {
	page?: number
	perPage?: number
	sortBy?: string
	sortOrder?: 'asc' | 'desc'
	location?: string
	listingInventory?: MMSubmissionInventory
	submissionType?: SubmissionType
	status?: MMSubmissionStatus
	// Splits a status into its first and second review outcomes
	hadSecondReview?: boolean
	search?: string
}

export const httpGetMMParkingLotListings = async (
	params: GetMMParkingLotListingsParams = {}
) => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/marketing-manager/parking-lot',
		{ params, paramsSerializer: { encode: encodeQueryParam } }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: MMParkingLotListingsResponse,
		onZodError: captureException
	})
}

export const GenerateMMParkingLotQuoteResponse = z.object({
	generated: z.array(
		z.object({
			submissionId: z.string(),
			quoteLink: z.string().nullable(),
			hostEmail: z.string().nullable()
		})
	),

	skipped: z.array(
		z.object({
			submissionId: z.string(),
			reason: z.string()
		})
	)
})

export type GenerateMMParkingLotQuoteResponse = z.infer<
	typeof GenerateMMParkingLotQuoteResponse
>
export interface GenerateMMParkingLotQuoteOptions {
	params: { submissionIds: string[]; blockEmail: boolean }
}

export const httpGenerateMMParkingLotQuote = async ({
	params
}: GenerateMMParkingLotQuoteOptions) => {
	const response = await axiosNode.post(
		'v3/luminate/luxe/marketing-manager/submissions/generate-quotes',
		params
	)
	return parseResponseData({
		responseData: response.data,
		responseShape: GenerateMMParkingLotQuoteResponse,
		onZodError: captureException
	})
}

export const httpGetFinalReviewListingActivity = async (listingId: string) => {
	const response = await axiosNode.get(
		`v3/luminate/luxe/final-review/submissions/${listingId}/activity`
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: SubmissionActivityEntry.array(),
		onZodError: captureException
	})
}

export const httpGetMMListingActivity = async (submissionId: string) => {
	const response = await axiosNode.get(
		`v3/luminate/luxe/marketing-manager/submissions/${submissionId}/activity`
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: z.object({ data: SubmissionActivityEntry.array() }),
		onZodError: captureException
	})
}

export const AskSecondReviewResponse = z.object({
	created: z.array(
		z.object({
			submissionId: z.string()
		})
	),
	skipped: z.array(
		z.object({
			submissionId: z.string(),
			reason: z.string()
		})
	)
})

export type AskSecondReviewResponse = z.infer<typeof AskSecondReviewResponse>

export interface AskSecondReviewPayload {
	submissionIds: string[]
	note: string
}

export const httpAskSecondReview = async (payload: AskSecondReviewPayload) => {
	const response = await axiosNode.post(
		'v3/luminate/luxe/marketing-manager/submissions/request-second-review',
		payload
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: AskSecondReviewResponse,
		onZodError: captureException
	})
}

// MM Data Overview
export interface GetMMDataOverviewParams {
	startDate?: string
	endDate?: string
}

export const httpGetMMDataOverview = async (
	params: GetMMDataOverviewParams = {}
) => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/marketing-manager/data-overview',
		{ params }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: z.object({
			data: MMDataOverview
		}),
		onZodError: captureException
	})
}

export interface GetVisualVettingVolumeParams {
	granularity: 'daily' | 'weekly' | 'monthly'
	from: string
	to: string
}

export const httpGetVisualVettingVolume = async (
	params: GetVisualVettingVolumeParams
) => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/visual-vetting/dashboard/volume',
		{ params }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: VisualVettingVolumeResponse,
		onZodError: captureException
	})
}

export interface GetVisualVettingAccuracyParams {
	granularity: 'daily' | 'weekly' | 'monthly'
	from: string
	to: string
}

export const httpGetVisualVettingAccuracy = async (
	params: GetVisualVettingAccuracyParams
) => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/visual-vetting/dashboard/accuracy',
		{ params }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: VisualVettingAccuracyResponse,
		onZodError: captureException
	})
}

export const SubmitListingResponse = z.object({
	externalHostId: z.string().nullable().optional(),
	listingName: z.string(),
	id: z.string(),
	airbnbListingId: z.string().nullable()
})

export type SubmitListingResponse = z.infer<typeof SubmitListingResponse>
export interface SubmitAirbnbListingPayload {
	airbnbUrl: string
	listingInventory: string
	hostEmail: string
	listingName: string
	geo: string
	bedrooms: number
	bathrooms: number
	beds: number
	guestCount: number
	propertyType: string | null
	amenities?: string[]
	photos?: string[]
	description: string | null
	hostName: string | null
	country: string | null
	hostIsSuperhost: boolean
	hostProfilePictureUrl: string | null
	lat: string | null
	lng: string | null
	roomType: string | null
	hostExternalId: string | null
	walkthroughContactEmail?: string
}

export const httpSubmitAirbnbListing = async (
	payload: SubmitAirbnbListingPayload
) => {
	const { data } = await axiosNode.post(
		'v3/luminate/luxe/marketing-manager/submissions/airbnb',
		payload
	)

	return parseResponseData({
		responseData: data.data,
		responseShape: SubmitListingResponse,
		onZodError: captureException
	})
}

export interface SubmitOtherListingPayload {
	listingName: string
	hostEmail: string
	geo: string
	listingInventory: string
	bedrooms: number
	bathrooms: number
	beds: number
	listingUrl?: string
	photoRepositoryUrl?: string
	galleryId?: string
	walkthroughContactEmail?: string
}

export const httpSubmitOtherListing = async (
	payload: SubmitOtherListingPayload
) => {
	const { data } = await axiosNode.post(
		'v3/luminate/luxe/marketing-manager/submissions/other',
		payload
	)

	return parseResponseData({
		responseData: data.data,
		responseShape: SubmitListingResponse,
		onZodError: captureException
	})
}

export const httpGetMMCounts = async () => {
	const response = await axiosNode.get(
		'v3/luminate/luxe/marketing-manager/counts'
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: z.object({
			data: z.object({
				submissions: z.number(),
				parkingLot: z.number()
			})
		}),
		onZodError: captureException
	})
}

// Create gallery
export const httpCreateLuxeGallery = async () => {
	const response = await axiosNode.post('v3/luminate/luxe/galleries')
	return parseResponseData({
		responseData: response.data,
		responseShape: z.object({
			galleryId: z.string()
		}),
		onZodError: captureException
	})
}

// Request upload links
export const HttpLuxeGalleryImage = z.object({
	uploadUrls: z.array(
		z.object({
			imageId: z.string(),
			preSignedUrl: z.url(),
			pathLocation: z.string(),
			fileName: z.string()
		})
	)
})

export type HttpLuxeGalleryImage = z.infer<typeof HttpLuxeGalleryImage>

export interface HttpRequestLuxeUploadLinksOptions {
	galleryId: string
	payload: { photoFileNames: string[] }
}

export const httpRequestLuxeUploadPhotosLinks = async ({
	galleryId,
	payload
}: HttpRequestLuxeUploadLinksOptions) => {
	const response = await axiosNode.post(
		`/v3/luminate/luxe/galleries/${galleryId}/images`,
		payload
	)
	return parseResponseData({
		responseData: response.data,
		responseShape: HttpLuxeGalleryImage,
		onZodError: captureException
	})
}

// Confirm upload

export interface HttpConfirmLuxeUploadOptions {
	urlParams: {
		galleryId: string
		imageId: string
	}
}

export const httpConfirmLuxeUpload = async ({
	urlParams: { galleryId, imageId }
}: HttpConfirmLuxeUploadOptions) => {
	return await axiosNode.post(
		`/v3/luminate/luxe/galleries/${galleryId}/images/${imageId}/confirm`
	)
}

// Delete image from gallery
export interface HttpDeleteLuxeGalleryImageOptions {
	urlParams: {
		galleryId: string
		imageId: string
	}
}

export const httpDeleteLuxeGalleryImage = ({
	urlParams: { galleryId, imageId }
}: HttpDeleteLuxeGalleryImageOptions) =>
	axiosNode.delete(
		`/v3/luminate/luxe/galleries/${galleryId}/images/${imageId}`
	)

// CSV Uploads
export interface GetCsvUploadsParams {
	page?: number
	perPage?: number
}

export const httpGetCsvUploads = async (params: GetCsvUploadsParams = {}) => {
	const response = await axiosNode.get('v3/luminate/luxe/csv-uploads', {
		params
	})

	return parseResponseData({
		responseData: response.data,
		responseShape: CsvUploadsResponse,
		onZodError: captureException
	})
}

export const httpGetCsvUpload = async (id: string) => {
	const response = await axiosNode.get(`v3/luminate/luxe/csv-uploads/${id}`)

	return parseResponseData({
		responseData: response.data,
		responseShape: z.object({ upload: CsvUpload }),
		onZodError: captureException
	})
}

export const CreateCsvUploadResponse = z.object({
	id: z.string(),
	preSignedUrl: z.url(),
	storagePath: z.string()
})
export type CreateCsvUploadResponse = z.infer<typeof CreateCsvUploadResponse>

export const httpCreateCsvUpload = async (fileName: string) => {
	const response = await axiosNode.post('v3/luminate/luxe/csv-uploads', {
		fileName
	})

	return parseResponseData({
		responseData: response.data,
		responseShape: CreateCsvUploadResponse,
		onZodError: captureException
	})
}

export const httpConfirmCsvUpload = (id: string) =>
	axiosNode.post(`v3/luminate/luxe/csv-uploads/${id}/confirm`)

export const httpDeleteCsvUpload = (id: string) =>
	axiosNode.delete(`v3/luminate/luxe/csv-uploads/${id}`)

export const CsvSchemaColumn = z.object({
	name: z.string(),
	type: z.string(),
	required: z.boolean(),
	recommended: z.boolean(),
	description: z.string(),
	example: z.string()
})
export type CsvSchemaColumn = z.infer<typeof CsvSchemaColumn>

export const CsvSchemaResponse = z.object({
	columns: z.array(CsvSchemaColumn)
})
export type CsvSchemaResponse = z.infer<typeof CsvSchemaResponse>

export const httpGetCsvUploadSchema = async () => {
	const response = await axiosNode.get('v3/luminate/luxe/csv-uploads/schema')

	return parseResponseData({
		responseData: response.data,
		responseShape: CsvSchemaResponse,
		onZodError: captureException
	})
}

export interface HttpRetryCsvUploadRowOptions {
	urlParams: { id: string; rowId: string }
}

export const httpRetryCsvUploadRow = ({
	urlParams: { id, rowId }
}: HttpRetryCsvUploadRowOptions) =>
	axiosNode.post(`v3/luminate/luxe/csv-uploads/${id}/rows/${rowId}/retry`)

export const httpGetLuxeFeatureFlags = async () => {
	const response = await axiosNode.get('/v3/luminate/luxe/feature-flags')

	return parseResponseData({
		responseData: response.data,
		responseShape: LuxeFeatureFlagsResponse,
		onZodError: captureException
	})
}
